import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { UTILITIES, getUtil } from "@/constants/utilities";
import { AS_HINT_LIST } from "@/constants/asSymbols";
import { BASEMAPS, CUSTOM_BASEMAP_KEY, loadCustomBasemap, saveCustomBasemap } from "@/lib/basemaps";
import { Layers } from "lucide-react";

// Great-circle distance between two [lat,lng] points in metres (Haversine).
const distMeters = (a, b) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2
          + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

// Length of an entire polyline (sum of Haversine segments) in metres.
const lineLengthMeters = (coords) => {
  let total = 0;
  for (let i = 1; i < coords.length; i += 1) total += distMeters(coords[i - 1], coords[i]);
  return total;
};

// Midpoint of a polyline (used to anchor the distance tooltip).
const lineMidpoint = (coords) => {
  if (!coords || coords.length === 0) return [0, 0];
  if (coords.length === 1) return coords[0];
  const target = lineLengthMeters(coords) / 2;
  let acc = 0;
  for (let i = 1; i < coords.length; i += 1) {
    const seg = distMeters(coords[i - 1], coords[i]);
    if (acc + seg >= target) {
      const t = (target - acc) / seg;
      return [
        coords[i - 1][0] + (coords[i][0] - coords[i - 1][0]) * t,
        coords[i - 1][1] + (coords[i][1] - coords[i - 1][1]) * t,
      ];
    }
    acc += seg;
  }
  return coords[coords.length - 1];
};

const formatDistance = (m) => (m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${m.toFixed(2)} m`);

// Leaflet scale bar — genuinely useful for onsite distance sanity checks.
const ScaleBar = () => {
  const map = useMap();
  useEffect(() => {
    const ctrl = L.control.scale({ imperial: false, metric: true, maxWidth: 140, position: "bottomleft" });
    ctrl.addTo(map);
    return () => { ctrl.remove(); };
  }, [map]);
  return null;
};

// Custom colored pin icon
const makeIcon = (color) => L.divIcon({
  className: "",
  html: `<div style="
    width:26px;height:26px;border-radius:50% 50% 50% 0;
    background:${color};border:2px solid #050505;
    transform:rotate(-45deg);
    box-shadow:2px 2px 0 0 #050505;
    display:flex;align-items:center;justify-content:center;">
    <div style="width:8px;height:8px;background:#fff;border-radius:50%;border:1.5px solid #050505;transform:rotate(45deg)"></div>
  </div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

const Recenter = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom ?? map.getZoom());
    // Intentionally only reacts to explicit center/zoom prop changes so we
    // don't fight the user's own pan/zoom gestures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.[0], center?.[1], zoom]);
  return null;
};

// Persists the map view (centre + zoom) whenever the user pans or zooms.
// Debounced so we don't hammer the backend during a fluid drag.
const ViewPersister = ({ onView }) => {
  const map = useMap();
  useEffect(() => {
    let timer = null;
    const push = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const c = map.getCenter();
        onView({ view_lat: c.lat, view_lng: c.lng, view_zoom: map.getZoom() });
      }, 500);
    };
    map.on("moveend", push);
    map.on("zoomend", push);
    return () => {
      if (timer) clearTimeout(timer);
      map.off("moveend", push);
      map.off("zoomend", push);
    };
  }, [map, onView]);
  return null;
};

const ClickHandler = ({ mode, activeService, onAddPin, onAddLinePoint }) => {
  useMapEvents({
    click(e) {
      if (mode === "pin") onAddPin(e.latlng.lat, e.latlng.lng);
      else if (mode === "line") onAddLinePoint(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const MapView = ({ job, activeService, mode, onChange, onPinClick }) => {
  const [drawingLine, setDrawingLine] = useState(null); // {service, coords: [[lat,lng]...]}
  const [basemapKey, setBasemapKey] = useState(() => localStorage.getItem("fp_basemap") || "esri-sat");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customUrl, setCustomUrl] = useState(() => (loadCustomBasemap() || {}).url || "");
  const [customLabel, setCustomLabel] = useState(() => (loadCustomBasemap() || {}).label || "");
  const center = useMemo(() => {
    // Prefer the last-viewed centre so re-opening a job returns to the exact
    // spot the tradie was working on. Fall back to the site GPS, then Sydney.
    const lat = job.view_lat ?? job.center_lat ?? -33.8688;
    const lng = job.view_lng ?? job.center_lng ?? 151.2093;
    return [lat, lng];
  }, [job.view_lat, job.view_lng, job.center_lat, job.center_lng]);
  const initialZoom = job.view_zoom ?? 18;

  const activeBasemap = useMemo(() => {
    if (basemapKey === CUSTOM_BASEMAP_KEY) {
      const c = loadCustomBasemap();
      if (c && c.url) return { key: CUSTOM_BASEMAP_KEY, label: c.label || "Custom", url: c.url, attribution: "Custom source", maxZoom: 22, maxNativeZoom: 20 };
      return BASEMAPS[0]; // fallback
    }
    return BASEMAPS.find((b) => b.key === basemapKey) || BASEMAPS[0];
  }, [basemapKey]);

  const chooseBasemap = (key) => {
    setBasemapKey(key);
    localStorage.setItem("fp_basemap", key);
    setPickerOpen(false);
  };

  const saveCustomAndSelect = () => {
    if (!customUrl.trim()) return;
    saveCustomBasemap({ url: customUrl.trim(), label: customLabel.trim() || "Custom" });
    chooseBasemap(CUSTOM_BASEMAP_KEY);
  };

  const addPin = (lat, lng) => {
    const util = getUtil(activeService);
    const depthRaw = window.prompt(`Depth of ${util.label} (mm, optional)`, "");
    const labelRaw = window.prompt(
      `Label / AS symbol code (optional) — ${AS_HINT_LIST}`,
      ""
    );
    const depth_mm = depthRaw && !isNaN(Number(depthRaw)) ? Number(depthRaw) : null;
    const pin = {
      id: crypto.randomUUID(),
      service: activeService,
      lat, lng,
      label: (labelRaw || "").trim(),
      notes: "",
      depth_mm,
    };
    onChange({ pins: [...(job.pins || []), pin] });
  };

  const addLinePoint = (lat, lng) => {
    // If a line is already in progress, keep extending it — do NOT reset when
    // the active service changes; the drawing's service is fixed at line start.
    if (!drawingLine) {
      setDrawingLine({ service: activeService, coords: [[lat, lng]] });
    } else {
      setDrawingLine({ ...drawingLine, coords: [...drawingLine.coords, [lat, lng]] });
    }
  };

  const commitLine = () => {
    if (drawingLine && drawingLine.coords.length >= 2) {
      const line = { id: crypto.randomUUID(), ...drawingLine, label: "" };
      onChange({ lines: [...(job.lines || []), line] });
    }
    setDrawingLine(null);
  };

  const removePin = (id) => onChange({ pins: (job.pins || []).filter(p => p.id !== id) });
  const removeLine = (id) => onChange({ lines: (job.lines || []).filter(l => l.id !== id) });

  return (
    <div id="fieldplan-map-capture" className="relative w-full h-full">
      <MapContainer
        center={center}
        zoom={initialZoom}
        maxZoom={22}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
      >
        <Recenter center={center} zoom={initialZoom} />
        <ViewPersister onView={onChange} />
        <ScaleBar />
        <TileLayer
          key={activeBasemap.key + activeBasemap.url}
          attribution={activeBasemap.attribution}
          url={activeBasemap.url}
          maxZoom={activeBasemap.maxZoom || 22}
          maxNativeZoom={activeBasemap.maxNativeZoom || 19}
          crossOrigin="anonymous"
        />
        <ClickHandler mode={mode} activeService={activeService} onAddPin={addPin} onAddLinePoint={addLinePoint} />

        {(job.pins || []).map(p => {
          const util = getUtil(p.service);
          const parts = [];
          if (p.label) parts.push(p.label);
          if (p.depth_mm != null) parts.push(`@ ${p.depth_mm}mm`);
          const tip = parts.length ? `${util.label}: ${parts.join(" ")}` : util.label;
          return (
            <Marker key={p.id} position={[p.lat, p.lng]} icon={makeIcon(util.color)}
              eventHandlers={{
                click: () => {
                  // Prefer opening the pin details dialog so users can add
                  // photos, edit label, and see the gallery. Falls back to
                  // the legacy delete-confirm if the host didn't wire the
                  // handler through.
                  if (typeof onPinClick === "function") onPinClick(p);
                  else if (window.confirm(`Remove ${util.label} pin?`)) removePin(p.id);
                },
              }}
            >
              <Tooltip permanent={!!(p.label || p.depth_mm != null)} direction="right" offset={[10, -10]} opacity={1}
                className="!bg-background !text-foreground !border-2 !border-foreground !font-mono !text-[10px] !uppercase !tracking-widest !rounded-none !shadow-none">
                {tip}
              </Tooltip>
            </Marker>
          );
        })}

        {(job.lines || []).map(ln => {
          const util = getUtil(ln.service);
          const pathOptions = {
            color: util.color,
            weight: 5,
            opacity: 0.9,
            ...(util.dash ? { dashArray: util.dash } : {}),
          };
          // Measurement utilities (e.g. Offset) get a permanent distance label
          // pinned to the midpoint of the polyline, so tradies can read it at
          // a glance without hovering.
          const showDistance = !!util.measure && ln.coords && ln.coords.length >= 2;
          const distanceText = showDistance ? formatDistance(lineLengthMeters(ln.coords)) : null;
          const midpoint = showDistance ? lineMidpoint(ln.coords) : null;
          return (
            <React.Fragment key={ln.id}>
              <Polyline positions={ln.coords} pathOptions={pathOptions}
                eventHandlers={{ click: () => { if (window.confirm(`Remove ${util.label} run?`)) removeLine(ln.id); } }}
              />
              {showDistance && (
                <Marker
                  position={midpoint}
                  interactive={false}
                  icon={L.divIcon({
                    className: "",
                    html: `<div style="
                      background:#111827;color:#fff;border:2px solid #050505;
                      padding:2px 6px;font-family:'JetBrains Mono',monospace;
                      font-size:10px;font-weight:700;letter-spacing:1px;
                      white-space:nowrap;box-shadow:2px 2px 0 0 #050505;">
                      ${distanceText}
                    </div>`,
                    iconSize: [1, 1],
                    iconAnchor: [0, 0],
                  })}
                />
              )}
            </React.Fragment>
          );
        })}

        {drawingLine && drawingLine.coords.length >= 1 && (
          <Polyline positions={drawingLine.coords}
            pathOptions={{
              color: getUtil(drawingLine.service).color,
              weight: 4,
              dashArray: getUtil(drawingLine.service).dash || "6 6",
            }}
          />
        )}
        {/* Live measurement readout while an Offset is being drawn. */}
        {drawingLine && getUtil(drawingLine.service).measure && drawingLine.coords.length >= 2 && (
          <Marker
            position={lineMidpoint(drawingLine.coords)}
            interactive={false}
            icon={L.divIcon({
              className: "",
              html: `<div style="
                background:#2563EB;color:#fff;border:2px solid #050505;
                padding:2px 6px;font-family:'JetBrains Mono',monospace;
                font-size:10px;font-weight:700;letter-spacing:1px;
                white-space:nowrap;box-shadow:2px 2px 0 0 #050505;">
                ${formatDistance(lineLengthMeters(drawingLine.coords))}
              </div>`,
              iconSize: [1, 1],
              iconAnchor: [0, 0],
            })}
          />
        )}
      </MapContainer>

      {/* Basemap picker */}
      <div className="absolute top-3 right-3 z-[1000]">
        <button data-testid="basemap-toggle" onClick={() => setPickerOpen((v) => !v)}
          className="btn-rugged text-[11px] py-1.5 px-2 inline-flex items-center gap-2">
          <Layers className="w-3.5 h-3.5" /> {activeBasemap.label}
        </button>
        {pickerOpen && (
          <div className="mt-2 border-2 border-foreground bg-background shadow-block-sm w-64 p-2 space-y-1"
            data-testid="basemap-picker">
            {BASEMAPS.map((b) => (
              <button key={b.key} data-testid={`basemap-${b.key}`} onClick={() => chooseBasemap(b.key)}
                className={`w-full text-left px-2 py-1.5 border-2 font-heading font-bold uppercase text-[11px] tracking-widest ${basemapKey === b.key ? "bg-foreground text-background border-foreground" : "border-foreground bg-background hover:bg-muted"}`}>
                {b.label}
              </button>
            ))}
            <div className="pt-2 border-t-2 border-foreground/40">
              <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Custom XYZ URL</div>
              <input value={customLabel} onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="Label (e.g. NearMap)"
                data-testid="basemap-custom-label"
                className="w-full border-2 border-foreground bg-background px-2 py-1 font-mono text-[11px]" />
              <input value={customUrl} onChange={(e) => setCustomUrl(e.target.value)}
                placeholder="https://tiles.example.com/{z}/{x}/{y}.png"
                data-testid="basemap-custom-url"
                className="mt-1 w-full border-2 border-foreground bg-background px-2 py-1 font-mono text-[10px]" />
              <button onClick={saveCustomAndSelect} data-testid="basemap-custom-save"
                className="mt-1 btn-rugged w-full text-[10px] py-1.5">Use custom map</button>
              <div className="mt-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground leading-relaxed">
                NearMap / Metromap / Mapbox subscribers: paste your keyed XYZ URL.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Line-drawing overlay controls */}
      {mode === "line" && (
        <div className="absolute top-3 left-3 z-[1000] bg-background border-2 border-foreground p-2 flex items-center gap-2 shadow-block-sm">
          <span className="font-mono text-[11px] uppercase tracking-widest px-1">
            Line: {drawingLine ? drawingLine.coords.length : 0} pts
          </span>
          <button data-testid="line-finish"
            onClick={commitLine}
            disabled={!drawingLine || drawingLine.coords.length < 2}
            className="btn-rugged text-[11px] py-1.5 px-2 disabled:opacity-50">Finish</button>
          <button data-testid="line-cancel"
            onClick={() => setDrawingLine(null)}
            className="btn-ghost-rugged text-[11px] py-1.5 px-2">Cancel</button>
        </div>
      )}
    </div>
  );
};

export default MapView;
